/**
 * Cache utility functions for client-side caching
 */

const CACHE_PREFIX = 'artmosphere_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default

/**
 * Create a cache key from a base key and optional parameters
 * @param {string} baseKey - The base cache key
 * @param {object} params - Optional parameters to include in the key
 * @param {string} userId - Optional user ID to include in the key (for user-specific cache)
 * @returns {string} - The generated cache key
 */
export function createCacheKey(baseKey, params = {}, userId = null) {
  if (!baseKey || typeof baseKey !== 'string') {
    throw new Error('createCacheKey: baseKey must be a non-empty string');
  }
  
  const keyParts = [baseKey];
  
  // Add user ID if provided
  if (userId) {
    keyParts.push(`user:${userId}`);
  }
  
  if (params && typeof params === 'object') {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${JSON.stringify(params[key])}`)
      .join('|');
    if (sortedParams) {
      keyParts.push(sortedParams);
    }
  }
  
  return `${CACHE_PREFIX}${keyParts.join('_')}`;
}

/**
 * Get a value from cache
 * @param {string} key - The cache key (can be baseKey or full cache key)
 * @param {object} params - Optional parameters if using baseKey
 * @returns {any|null} - The cached value or null if not found/expired
 */
export function getCache(key, params = null) {
  try {
    const cacheKey = params ? createCacheKey(key, params) : key;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const parsed = JSON.parse(cached);
    const now = Date.now();
    
    // Check if expired
    if (parsed.expiresAt && now > parsed.expiresAt) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return parsed.value;
  } catch (error) {
    console.error('Error getting cache:', error);
    return null;
  }
}

/**
 * Set a value in cache
 * @param {string} key - The cache key (can be baseKey or full cache key)
 * @param {any} value - The value to cache
 * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
 * @param {object} params - Optional parameters if using baseKey
 */
export function setCache(key, value, ttl = DEFAULT_TTL, params = null) {
  try {
    const cacheKey = params ? createCacheKey(key, params) : key;
    const expiresAt = Date.now() + ttl;
    
    const cacheData = {
      value,
      expiresAt,
      cachedAt: Date.now()
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error setting cache:', error);
    // If quota exceeded, try to clear old cache entries
    if (error.name === 'QuotaExceededError') {
      clearOldCache();
      try {
        const cacheKey = params ? createCacheKey(key, params) : key;
        const expiresAt = Date.now() + ttl;
        const cacheData = {
          value,
          expiresAt,
          cachedAt: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (retryError) {
        console.error('Error setting cache after cleanup:', retryError);
      }
    }
  }
}

/**
 * Clear a specific cache entry
 * @param {string} key - The cache key (can be baseKey or full cache key)
 * @param {object} params - Optional parameters if using baseKey
 */
export function clearCache(key, params = null) {
  try {
    const cacheKey = params ? createCacheKey(key, params) : key;
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
}

/**
 * Clear all cache entries with the prefix
 */
export function clearAllCache() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all cache:', error);
  }
}

/**
 * Clear old/expired cache entries
 */
function clearOldCache() {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    let cleared = 0;
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.expiresAt && now > parsed.expiresAt) {
              localStorage.removeItem(key);
              cleared++;
            }
          }
        } catch (e) {
          // Invalid cache entry, remove it
          localStorage.removeItem(key);
          cleared++;
        }
      }
    });
    
    if (cleared > 0) {
      console.log(`Cleared ${cleared} expired cache entries`);
    }
  } catch (error) {
    console.error('Error clearing old cache:', error);
  }
}

// Clear old cache on module load
if (typeof window !== 'undefined') {
  clearOldCache();
}

